import {
  Box,
  Button,
  Grid,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
} from '@chakra-ui/react';
import { NODE_ADDED } from 'constants/mixpanel';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { useAppSelector } from 'modules/Editor/store/hooks';
import { selectWidgetLayerOrder, selectWidgets } from 'modules/Editor/store/infographSelector';
import { selectActivePage } from 'modules/Editor/store/pageSelector';
import { memo, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ConnectedWidgetData } from 'types/flowCore.types';
import { WidgetId } from 'types/idTypes';
import { WidgetsMap } from 'types/infographTypes';
import { WidgetType } from 'types/widget.types';
import { getGroupTargetDataFromWidgetId } from 'utils/dom';
import { Pos } from 'widgets/Lines/LineWidget/LineWidget.types';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';
import { GroupWidgetData } from 'widgets/Widget.types';
import { NewWidget } from 'widgets/Widget.types';
import { getWidgetComponent } from 'widgets/WidgetRenderer';

import { useBoundingBox } from '../../BoundingBox/useBoundingBox/useBoundingBox';
import { BUTTON_GRID_GAP, BUTTON_GRID_PX, PADDING_PX } from '../FlowCore.config';
import { getConnectedLineData, getConnectedWidgetData } from '../FlowCore.helpers';
import {
  duplicateNodeWidget,
  generateAddNodeWidgets,
  getButtonWidgetStyle,
  getWidgetDimensionsFromConnectedWidgetData,
  getWidgetScale,
} from '../FlowCore.helpers';
import { useFlowPort } from '../Port.hooks';

interface NodeCreatorModalProps {
  zoom: number;
}

const NodeCreatorModal = ({ zoom }: NodeCreatorModalProps) => {
  const { nodeModalData, closeModal: closeNodeModal } = useFlowPort();

  const isOpen = nodeModalData !== null;

  // Prevent to change the position during modal closing
  const lastNodeModalPosition = useRef<Pos>({ xPx: 0, yPx: 0 });
  if (nodeModalData !== null) {
    lastNodeModalPosition.current.xPx = nodeModalData?.position.leftPx || 0;
    lastNodeModalPosition.current.yPx = nodeModalData?.position.topPx || 0;
  }

  return (
    <Box>
      <Popover returnFocusOnClose={false} isOpen={isOpen} onClose={closeNodeModal} placement='bottom' isLazy>
        <PopoverTrigger>
          {/* Set position box for rendering the Modal */}
          <Box
            position='absolute'
            left={`${lastNodeModalPosition.current.xPx * zoom - PADDING_PX / 2}px`}
            top={`${lastNodeModalPosition.current.yPx * zoom - PADDING_PX / 2}px`}
            width={`${PADDING_PX}px`}
            height={`${PADDING_PX}px`}
          />
        </PopoverTrigger>
        <PopoverContent pointerEvents='auto'>
          <NodeCreatorModalContent />
        </PopoverContent>
      </Popover>
    </Box>
  );
};

const NodeCreatorModalContent = () => {
  const { t } = useTranslation('editor_flowmode', { useSuspense: false });

  const { nodeModalData, closeModal: closeNodeModal } = useFlowPort();

  const activePageId = useAppSelector(selectActivePage);
  const widgetLayerOrder = useAppSelector(selectWidgetLayerOrder(activePageId));
  // TODO: might need to reconsider this heavy selector - but only active when the add node modal is open
  const widgets = useAppSelector(selectWidgets);

  const { widgetRefs } = useBoundingBox();
  const widgetIds = Object.keys(widgetRefs);
  const renderedWidgets = useMemo(() => {
    return Object.keys(widgets)
      .filter((widgetId) => widgetIds.includes(widgetId))
      .reduce((prev: WidgetsMap, cur: WidgetId) => {
        prev[cur] = widgets[cur];
        return prev;
      }, {});
  }, [widgets, widgetIds]);

  const connectedLineData = getConnectedLineData(renderedWidgets);
  const connectedWidgetData = getConnectedWidgetData(renderedWidgets, connectedLineData);
  const connectedWidgetDataList = generateAddNodeWidgets(
    renderedWidgets,
    connectedWidgetData,
    nodeModalData?.widgetId,
    widgetLayerOrder,
  );

  const handleAddWidget = useCallback(
    (widgetId: WidgetId) => {
      if (!nodeModalData?.lineWidgetData) return;

      duplicateNodeWidget(widgetId, nodeModalData.lineWidgetData as NewWidget);
      closeNodeModal();

      Mixpanel.track(NODE_ADDED.eventName, {
        isSameNode: nodeModalData.widgetId === widgetId,
      });
    },
    [nodeModalData, closeNodeModal],
  );

  const addSelectedWidget = useCallback(() => {
    if (!nodeModalData?.widgetId) return;
    handleAddWidget(nodeModalData.widgetId);
  }, [nodeModalData, handleAddWidget]);

  const renderWidget = useCallback((data: ConnectedWidgetData) => {
    const WidgetComponent = getWidgetComponent();
    const widgetType = getWidgetTypeFromId(data.widgetId);
    let customWidgetData = { leftPx: 0, topPx: 0 };
    if (widgetType === WidgetType.Group) {
      // group widget renders child widgets with their own absolute positioning from 0,0 canvas. We need to offset
      // the widget to render it in the center of the button. find the leftmost and topmost widget and offset by that
      // in the negative direction
      const groupTargetData = getGroupTargetDataFromWidgetId(data.widgetId, data.widgetData as GroupWidgetData);
      if (!!groupTargetData) {
        customWidgetData = {
          leftPx: -groupTargetData.wrapperPos.leftPx,
          topPx: -groupTargetData.wrapperPos.topPx,
        };
      }
    }

    return (
      <WidgetComponent
        isReadOnly={true}
        widgetId={data.widgetId}
        zIndex={0}
        getWidgetMemberComponent={getWidgetComponent}
        customWidgetData={customWidgetData}
      />
    );
  }, []);

  return (
    <>
      <PopoverHeader fontWeight='semibold' textAlign='center'>
        <Button colorScheme={'green'} onClick={addSelectedWidget}>
          {t('modal.add-widget-button')}
        </Button>
      </PopoverHeader>
      {connectedWidgetDataList.length ? (
        <>
          <PopoverArrow />
          <PopoverBody display='grid' overflowY='scroll' overflowX='hidden' maxHeight={200}>
            <Grid gridTemplateColumns={`repeat(4, ${BUTTON_GRID_PX}px)`} gap={BUTTON_GRID_GAP}>
              {connectedWidgetDataList.map((widgetData) => {
                const { heightPx, widthPx } = getWidgetDimensionsFromConnectedWidgetData(widgetData);
                return (
                  <Button
                    key={widgetData.widgetId}
                    width={`${BUTTON_GRID_PX}px`}
                    height={`${BUTTON_GRID_PX}px`}
                    overflow='hidden'
                    variant='add-widget-button'
                    onClick={() => handleAddWidget(widgetData.widgetId)}
                  >
                    <Box
                      position='absolute'
                      {...getButtonWidgetStyle(heightPx, widthPx)}
                      transformOrigin='top left'
                      transform={`scale(${getWidgetScale(heightPx, widthPx)})`}
                    >
                      {renderWidget(widgetData)}
                    </Box>
                  </Button>
                );
              })}
            </Grid>
          </PopoverBody>
        </>
      ) : null}
    </>
  );
};

export default memo(NodeCreatorModal);
